/**
 * Pure helpers for the public payment-status page (/share#<token>).
 *
 * The page renders data for someone without an account, so everything the
 * relay returns is treated as untrusted: the view is re-built field by field
 * with type checks (a malformed amount must never reach BigInt and crash the
 * page), and step/progress logic lives here where it can be unit tested.
 */

import type {
  SharedApprovalStatus,
  SharedProposalErrorReason,
  SharedProposalStage,
  SharedProposalView,
  SharedSigningPayload,
  SharedTimelineEntry,
  SharedTimelineEvent,
} from '@/types/proposalShare';
import { SHARED_PROPOSAL_STAGES } from '@/types/proposalShare';
import type { ProposalRecipient, ProposalStatus } from '@/types/vault';

const UINT_PATTERN = /^\d{1,78}$/;
/** Loose shape check — the relay validates the exact format. */
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,256}$/;
const MIN_REFRESH_SEC = 5;
const MAX_REFRESH_SEC = 300;

const PROPOSAL_STATUSES: readonly ProposalStatus[] = [
  'pending',
  'pending_approval',
  'approved',
  'approved_pending_broadcast',
  'rejected',
  'cancelled',
  'broadcast',
  'confirmed',
  'expired',
  'failed',
  // Parallel proposals: can never land (coins spent / nonce used).
  'stale',
];

const TIMELINE_EVENTS: readonly SharedTimelineEvent[] = [
  'created',
  'approval_required',
  'approved',
  'approval_rejected',
  'signed',
  'scheduled',
  'broadcast',
  'confirmed',
  'reverted',
  'failed',
  'rejected',
  'cancelled',
  'expired',
];

const APPROVAL_STATUSES: readonly SharedApprovalStatus[] = ['pending', 'approved', 'rejected'];
const ROUND_STATUSES = ['pending', 'approved', 'rejected', 'timed_out'] as const;

/** Non-negative integer string (smallest-unit amounts, gas values). */
export function isUintString(value: unknown): value is string {
  return typeof value === 'string' && UINT_PATTERN.test(value);
}

/** Same check for a value already typed as string (no type narrowing). */
export function isUintText(value: string): boolean {
  return UINT_PATTERN.test(value);
}

/** Map the relay's errorCode for a share lookup to a page error state. */
export function shareErrorReasonFromCode(code: string | undefined): SharedProposalErrorReason {
  switch (code) {
    case 'SHARE_NOT_FOUND':
      return 'not_found';
    case 'SHARE_EXPIRED':
      return 'expired';
    case 'SHARE_REVOKED':
      return 'revoked';
    case 'SHARE_DISABLED':
      return 'disabled';
    case 'RATE_LIMITED':
      return 'rate_limited';
    default:
      return 'unavailable';
  }
}

/**
 * The token from a location hash (`#<token>`). Returns null when there is no
 * token, '' never. Invalid-looking tokens are returned as-is; callers decide
 * (see isPlausibleShareToken).
 */
export function readShareToken(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const token = raw.trim();
  return token ? token : null;
}

export function isPlausibleShareToken(token: string): boolean {
  return TOKEN_PATTERN.test(token);
}

/** Route of the public payment-status page. */
export const SHARE_ROUTE_PATH = '/share';

/** True on the public share page (tolerates a trailing slash). */
export function isShareRoutePath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === SHARE_ROUTE_PATH;
}

/**
 * The share URL for a token. The secret lives in the fragment only: browsers
 * never send fragments to servers, analytics or in the Referer header.
 */
export function buildShareUrl(origin: string, token: string): string {
  return `${origin}${SHARE_ROUTE_PATH}#${token}`;
}

// ============================================================
// View parsing
// ============================================================

type Obj = Record<string, unknown>;

function isObj(value: unknown): value is Obj {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function isDateString(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value));
}

function dateStr(value: unknown): string | undefined {
  return isDateString(value) ? value : undefined;
}

function count(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < 1000
    ? value
    : undefined;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

function parseTimelineEntry(raw: unknown): SharedTimelineEntry | null {
  if (!isObj(raw)) return null;
  const event = oneOf(raw.event, TIMELINE_EVENTS);
  const at = dateStr(raw.at);
  if (!event || !at) return null;
  const entry: SharedTimelineEntry = { event, at };
  if (raw.upcoming === true) entry.upcoming = true;
  const index = count(raw.index);
  const of = count(raw.of);
  if (index !== undefined) entry.index = index;
  if (of !== undefined) entry.of = of;
  const name = str(raw.name);
  if (name) entry.name = name;
  return entry;
}

function parseSigning(raw: unknown): SharedSigningPayload | undefined {
  if (!isObj(raw)) return undefined;
  if (raw.kind === 'utxo') {
    const inputAmounts = Array.isArray(raw.inputAmounts)
      ? raw.inputAmounts.filter(isUintString)
      : [];
    const rawUnsignedTx =
      typeof raw.rawUnsignedTx === 'string' && /^[0-9a-fA-F]+$/.test(raw.rawUnsignedTx)
        ? raw.rawUnsignedTx
        : undefined;
    return { kind: 'utxo', inputAmounts, ...(rawUnsignedTx ? { rawUnsignedTx } : {}) };
  }
  if (raw.kind === 'evm') {
    const s = (v: unknown) => (typeof v === 'string' ? v : '');
    return {
      kind: 'evm',
      sender: s(raw.sender),
      nonce: s(raw.nonce),
      callData: s(raw.callData),
      callGasLimit: s(raw.callGasLimit),
      verificationGasLimit: s(raw.verificationGasLimit),
      preVerificationGas: s(raw.preVerificationGas),
      maxFeePerGas: s(raw.maxFeePerGas),
      maxPriorityFeePerGas: s(raw.maxPriorityFeePerGas),
      deploysAccount: raw.deploysAccount === true,
    };
  }
  if (raw.kind === 'solana') {
    return { kind: 'solana', flow: raw.flow === 'split' ? 'split' : 'bundled' };
  }
  return undefined;
}

/**
 * Re-build a SharedProposalView from an untrusted response. Returns null when
 * a field the page cannot do without is missing or malformed (rather than
 * rendering a half-correct payment). Optional fields with the wrong type are
 * dropped.
 */
export function parseSharedProposalView(raw: unknown): SharedProposalView | null {
  if (!isObj(raw)) return null;

  const stage = oneOf(raw.stage, SHARED_PROPOSAL_STAGES);
  const status = oneOf(raw.status, PROPOSAL_STATUSES);
  const organizationName = str(raw.organizationName);
  const reference = str(raw.reference);
  const chain = str(raw.chain);
  const createdAt = dateStr(raw.createdAt);
  const link = isObj(raw.link) ? raw.link : null;
  const linkExpiresAt = link ? dateStr(link.expiresAt) : undefined;
  const sigs = isObj(raw.signatures) ? raw.signatures : null;
  const required = sigs ? count(sigs.required) : undefined;
  const collected = sigs ? count(sigs.collected) : undefined;
  if (
    !stage ||
    !status ||
    !organizationName ||
    !reference ||
    !chain ||
    !createdAt ||
    !linkExpiresAt ||
    required === undefined ||
    collected === undefined ||
    !Array.isArray(raw.recipients)
  ) {
    return null;
  }

  const recipients: ProposalRecipient[] = [];
  for (const r of raw.recipients) {
    if (!isObj(r) || !str(r.address) || !isUintString(r.amount)) return null;
    recipients.push({ address: r.address as string, amount: r.amount });
  }

  const timeline = Array.isArray(raw.timeline)
    ? raw.timeline.map(parseTimelineEntry).filter((e): e is SharedTimelineEntry => e !== null)
    : [];

  const view: SharedProposalView = {
    link: { expiresAt: linkExpiresAt },
    organizationName,
    reference,
    chain,
    status,
    stage,
    createdAt,
    updatedAt: dateStr(raw.updatedAt) ?? createdAt,
    expiresAt: dateStr(raw.expiresAt) ?? createdAt,
    recipients,
    signatures: { required, collected },
    timeline,
  };

  const label = link ? str(link.label) : undefined;
  if (label) view.link.label = label;

  const fromAddress = str(raw.fromAddress);
  if (fromAddress) view.fromAddress = fromAddress;
  const tokenContract = str(raw.tokenContract);
  if (tokenContract) view.tokenContract = tokenContract;
  const tokenSymbol = str(raw.tokenSymbol);
  if (tokenSymbol) view.tokenSymbol = tokenSymbol;
  if (
    typeof raw.tokenDecimals === 'number' &&
    Number.isInteger(raw.tokenDecimals) &&
    raw.tokenDecimals >= 0 &&
    raw.tokenDecimals <= 36
  ) {
    view.tokenDecimals = raw.tokenDecimals;
  }
  if (isUintString(raw.fee)) view.fee = raw.fee;
  if (typeof raw.amountUsd === 'number' && Number.isFinite(raw.amountUsd) && raw.amountUsd >= 0) {
    view.amountUsd = raw.amountUsd;
  }
  const memo = str(raw.memo);
  if (memo) view.memo = memo;

  if (isObj(raw.approval)) {
    const approvalStatus = oneOf(raw.approval.status, APPROVAL_STATUSES);
    if (approvalStatus) {
      view.approval = { status: approvalStatus };
      if (Array.isArray(raw.approval.rounds)) {
        const rounds: NonNullable<NonNullable<SharedProposalView['approval']>['rounds']> = [];
        for (const r of raw.approval.rounds) {
          if (!isObj(r)) continue;
          const roundStatus = oneOf(r.status, ROUND_STATUSES);
          const roundRequired = count(r.required);
          const approved = count(r.approved);
          if (roundStatus && roundRequired !== undefined && approved !== undefined) {
            rounds.push({ status: roundStatus, required: roundRequired, approved });
          }
        }
        if (rounds.length > 0) view.approval.rounds = rounds;
      }
    }
  }

  if (isObj(raw.timeLock)) {
    const scheduledBroadcastAt = dateStr(raw.timeLock.scheduledBroadcastAt);
    if (scheduledBroadcastAt) {
      view.timeLock = { scheduledBroadcastAt, cancelled: raw.timeLock.cancelled === true };
    }
  }

  const txid = str(raw.txid);
  if (txid) view.txid = txid;
  const userOpHash = str(raw.userOpHash);
  if (userOpHash) view.userOpHash = userOpHash;
  const broadcastAt = dateStr(raw.broadcastAt);
  if (broadcastAt) view.broadcastAt = broadcastAt;
  const confirmedAt = dateStr(raw.confirmedAt);
  if (confirmedAt) view.confirmedAt = confirmedAt;
  if (typeof raw.executionSuccess === 'boolean') view.executionSuccess = raw.executionSuccess;

  const signing = parseSigning(raw.signing);
  if (signing) view.signing = signing;

  if (typeof raw.refreshAfterSec === 'number' && Number.isFinite(raw.refreshAfterSec)) {
    view.refreshAfterSec = Math.min(
      MAX_REFRESH_SEC,
      Math.max(MIN_REFRESH_SEC, Math.round(raw.refreshAfterSec))
    );
  }

  return view;
}

/**
 * Values that are safe to splice into an explorer URL path: addresses and
 * hashes are alphanumeric (bech32/base58/hex), plus the cashaddr prefix colon.
 */
export function isLinkableChainValue(value: string): boolean {
  return /^[A-Za-z0-9:]{8,128}$/.test(value);
}

// ============================================================
// Progress steps
// ============================================================

export type ShareStepKey =
  | 'created'
  | 'approval'
  | 'signatures'
  | 'scheduled'
  | 'sent'
  | 'confirmed';
export type ShareStepStatus = 'finish' | 'process' | 'error';

export interface ShareProgress {
  steps: ShareStepKey[];
  /** Index into `steps` of the step the payment is at. */
  current: number;
  /** Status of the current step (earlier steps are done, later ones waiting). */
  status: ShareStepStatus;
}

const TERMINAL_STOPPED: ReadonlySet<SharedProposalStage> = new Set([
  'rejected',
  'cancelled',
  'expired',
]);

/**
 * Steps for the progress bar: Created → Approval (when there is one) →
 * Signatures → Scheduled (time lock, unless cancelled) → Sent → Confirmed,
 * with the current step and whether it is in progress, done or failed.
 */
export function computeShareProgress(view: SharedProposalView): ShareProgress {
  const steps: ShareStepKey[] = ['created'];
  if (view.approval) steps.push('approval');
  steps.push('signatures');
  if (view.timeLock && !view.timeLock.cancelled) steps.push('scheduled');
  steps.push('sent', 'confirmed');

  const at = (key: ShareStepKey) => {
    const i = steps.indexOf(key);
    return i === -1 ? steps.indexOf('signatures') : i;
  };

  switch (view.stage) {
    case 'awaiting_approval':
      return { steps, current: at('approval'), status: 'process' };
    case 'collecting_signatures':
      return { steps, current: at('signatures'), status: 'process' };
    case 'scheduled':
      return { steps, current: at('scheduled'), status: 'process' };
    case 'broadcasting':
      return { steps, current: at('sent'), status: 'process' };
    case 'sent':
      return { steps, current: at('confirmed'), status: 'process' };
    case 'confirmed':
      return { steps, current: steps.length - 1, status: 'finish' };
    case 'failed': {
      // Landed but reverted (EVM) → failed at confirmation; never made it
      // onto the network → failed at sending.
      const reachedNetwork =
        view.executionSuccess === false || Boolean(view.confirmedAt || view.txid);
      return { steps, current: at(reachedNetwork ? 'confirmed' : 'sent'), status: 'error' };
    }
    default:
      break;
  }

  // rejected / cancelled / expired — stopped where it was waiting.
  if (TERMINAL_STOPPED.has(view.stage)) {
    if (view.approval && view.approval.status !== 'approved') {
      return { steps, current: at('approval'), status: 'error' };
    }
    if (view.signatures.collected < view.signatures.required) {
      return { steps, current: at('signatures'), status: 'error' };
    }
    if (steps.includes('scheduled') && !view.broadcastAt) {
      return { steps, current: at('scheduled'), status: 'error' };
    }
    return { steps, current: at('sent'), status: 'error' };
  }
  return { steps, current: at('signatures'), status: 'process' };
}

/** Final stages: nothing will change any more. */
export function isFinalStage(stage: SharedProposalStage): boolean {
  return (
    stage === 'confirmed' ||
    stage === 'failed' ||
    stage === 'rejected' ||
    stage === 'cancelled' ||
    stage === 'expired'
  );
}
